import { Box, Typography, Chip, Skeleton, CircularProgress, Alert, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TIER_STYLE, scoreColor, scoreLabel, fmt, fmtPct } from './constants';

// ── Thin horizontal bar used inside SubScoreCell ──────────────────────────────
export const ScoreBar = ({ value, color = '#3b82f6', width = 60 }) => {
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <Box sx={{ width, height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ width: `${v}%`, height: '100%', bgcolor: color, transition: 'width 0.4s' }} />
    </Box>
  );
};

// ── Sub-score cell: label + mini bar + raw number ─────────────────────────────
export const SubScoreCell = ({ value, color, hint }) => {
  const { t } = useTranslation();
  const lbl = scoreLabel(value);
  const text = lbl.key ? t(lbl.key) : lbl.text;
  const tipTitle = hint
    ? t('icb.leaders.tooltips.subScore', { label: hint, value: value != null ? Number(value).toFixed(1) : '-' })
    : '';
  return (
    <Tooltip title={tipTitle} arrow>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
        <Typography fontSize="0.7rem" fontWeight={700} color={lbl.color} sx={{ lineHeight: 1.1 }}>
          {text}
        </Typography>
        <ScoreBar value={value} color={color} />
        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', lineHeight: 1 }}>
          {value != null ? Number(value).toFixed(0) : '-'}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ── Per-ICB stock leaderboard (drill-down panel) ─────────────────────────────
const LeadersTable = ({ state, groupName, hideHeader = false }) => {
  const { t } = useTranslation();

  if (!state || state.loading) {
    const SkelRow = ({ idx }) => (
      <TableRow sx={{ animation: `fadePulse 1.4s ease-in-out ${idx * 0.08}s infinite` }}>
        <TableCell align="center"><Skeleton variant="text" width={14} sx={{ mx: 'auto' }} /></TableCell>
        <TableCell>
          <Skeleton variant="text" width={42} height={14} />
          <Skeleton variant="text" width={110} height={10} />
        </TableCell>
        <TableCell align="center"><Skeleton variant="rounded" width={56} height={18} sx={{ mx: 'auto', borderRadius: 9 }} /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={28} sx={{ ml: 'auto' }} /></TableCell>
        {[0, 1, 2, 3].map(k => (
          <TableCell key={k} align="center">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
              <Skeleton variant="text" width={22} height={10} />
              <Skeleton variant="rounded" width={60} height={6} />
            </Box>
          </TableCell>
        ))}
        <TableCell align="right"><Skeleton variant="text" width={32} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={32} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={32} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={40} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center' }}>
            <Skeleton variant="rounded" width={32} height={16} />
            <Skeleton variant="rounded" width={26} height={16} />
          </Box>
        </TableCell>
      </TableRow>
    );
    return (
      <Box sx={{ bgcolor: '#f8fafc', borderBottom: '2px solid #cbd5e1', px: 2, py: 1.5,
        '@keyframes fadePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.55 } },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={12} thickness={6} />
            <Typography variant="subtitle2" fontWeight={700} fontSize="0.8rem" color="text.secondary">
              {t('icb.leaders.loading', { group: groupName })}
            </Typography>
          </Box>
          <Skeleton variant="text" width={220} height={14} />
        </Box>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
          <Table size="small" sx={{ '& th, & td': { fontSize: '0.72rem', py: 0.5 } }}>
            <TableHead sx={{ bgcolor: '#fff' }}>
              <TableRow>
                {['rank','ticker','tier','strength','rs','momentum','moneyFlow','trend','change1D','change5D','change20D','liquidity','alerts'].map(k => (
                  <TableCell key={k} align={['rank','tier','rs','momentum','moneyFlow','trend','alerts'].includes(k) ? 'center' : 'right'}
                    sx={{ fontWeight: 700, color: '#94a3b8' }}>{t(`icb.leaders.headers.${k}`)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{[0,1,2,3,4,5].map(i => <SkelRow key={i} idx={i} />)}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box sx={{ p: 2, bgcolor: '#fef2f2', borderBottom: '1px solid #e2e8f0' }}>
        <Alert severity="error">{state.error}</Alert>
      </Box>
    );
  }

  const data = state.data;
  if (!data?.leaders?.length) {
    return (
      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Typography variant="caption" color="text.secondary">{t('icb.leaders.noEligible')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: hideHeader ? 'transparent' : '#f8fafc', borderBottom: hideHeader ? 'none' : '2px solid #cbd5e1', px: hideHeader ? 0 : 2, py: hideHeader ? 0 : 1.5 }}>
      {!hideHeader && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} fontSize="0.8rem">
            {t('icb.leaders.innerHeader', { group: groupName })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('icb.leaders.metaInfo', { count: data.leader_count, date: data.score_date || '-' })}
          </Typography>
        </Box>
      )}
      <TableContainer component={Paper} elevation={0} sx={{ border: hideHeader ? 'none' : '1px solid #e2e8f0', borderRadius: hideHeader ? 0 : 1 }}>
        <Table size="small" sx={{
          '& th, & td': { fontSize: '0.72rem', py: 0.6 },
          '& tbody tr:nth-of-type(odd)': { bgcolor: '#fafbfc' },
          '& tbody tr:hover': { bgcolor: '#eff6ff !important' },
        }}>
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 700, width: 30 }}>{t('icb.leaders.headers.rank')}</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 90 }}>{t('icb.leaders.headers.ticker')}</TableCell>
              {['tier','strength','rs','momentum','moneyFlow','trend'].map(k => (
                <TableCell key={k} align={k === 'strength' ? 'right' : 'center'} sx={{ fontWeight: 700 }}>
                  <Tooltip title={t(`icb.leaders.tooltips.${k}`)} arrow>
                    <span>{t(`icb.leaders.headers.${k}`)}</span>
                  </Tooltip>
                </TableCell>
              ))}
              {['change1D','change5D','change20D'].map(k => (
                <TableCell key={k} align="right" sx={{ fontWeight: 700 }}>
                  <Tooltip title={t(`icb.leaders.tooltips.${k}`)} arrow><span>{t(`icb.leaders.headers.${k}`)}</span></Tooltip>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                <Tooltip title={t('icb.leaders.tooltips.liquidity')} arrow><span>{t('icb.leaders.headers.liquidity')}</span></Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>{t('icb.leaders.headers.alerts')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.leaders.map((L, i) => {
              const tier = TIER_STYLE[L.strength_tier] || TIER_STYLE.REST;
              const ss = L.sub_scores || {};
              const p = L.price || {};
              const sLbl = scoreLabel(L.strength_score);
              const sLblText = sLbl.key ? t(sLbl.key) : sLbl.text;
              const flags = [
                L.leader_flag        && { key: 'leader',       label: t('icb.leaders.flags.leader'),       color: '#15803d', bg: '#dcfce7', tip: t('icb.leaders.flags.leaderTip') },
                L.persistent_leader  && { key: 'persistent',   label: t('icb.leaders.flags.persistent'),   color: '#1d4ed8', bg: '#dbeafe', tip: t('icb.leaders.flags.persistentTip') },
                L.risk?.breakout_bar_flag    && { key: 'breakout',    label: t('icb.leaders.flags.breakout'),    color: '#0891b2', bg: '#cffafe', tip: t('icb.leaders.flags.breakoutTip') },
                L.risk?.manipulation_flag    && { key: 'manipulation',label: t('icb.leaders.flags.manipulation'),color: '#dc2626', bg: '#fee2e2', tip: t('icb.leaders.flags.manipulationTip') },
                L.risk?.exhausted_flag       && { key: 'exhausted',   label: t('icb.leaders.flags.exhausted'),  color: '#dc2626', bg: '#fee2e2', tip: t('icb.leaders.flags.exhaustedTip') },
                L.risk?.distribution_flag    && { key: 'distribution',label: t('icb.leaders.flags.distribution'),color: '#dc2626', bg: '#fee2e2', tip: t('icb.leaders.flags.distributionTip') },
                L.risk?.climax_top_flag      && { key: 'climax',      label: t('icb.leaders.flags.climax'),     color: '#dc2626', bg: '#fee2e2', tip: t('icb.leaders.flags.climaxTip') },
              ].filter(Boolean);

              return (
                <TableRow key={L.ticker} hover>
                  <TableCell align="center" sx={{ color: '#94a3b8', fontWeight: 700 }}>{i + 1}</TableCell>
                  <TableCell>
                    <Typography fontWeight={700} fontSize="0.78rem">{L.ticker}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }} noWrap>{L.company_name}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={t(`icb.tier.${L.strength_tier || 'REST'}`)} size="small"
                      sx={{ bgcolor: tier.bg, color: tier.color, fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.1 }}>
                      <Typography fontWeight={700} fontSize="0.85rem" color={scoreColor(L.strength_score)} sx={{ lineHeight: 1 }}>
                        {fmt(L.strength_score, 0)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: sLbl.color, fontWeight: 700, lineHeight: 1 }}>{sLblText}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center"><SubScoreCell value={ss.relative_strength} color="#3b82f6" hint={t('icb.leaders.headers.rs')} /></TableCell>
                  <TableCell align="center"><SubScoreCell value={ss.momentum_quality}  color="#8b5cf6" hint={t('icb.leaders.headers.momentum')} /></TableCell>
                  <TableCell align="center"><SubScoreCell value={ss.money_flow}        color="#10b981" hint={t('icb.leaders.headers.moneyFlow')} /></TableCell>
                  <TableCell align="center"><SubScoreCell value={ss.trend_quality}     color="#f59e0b" hint={t('icb.leaders.headers.trend')} /></TableCell>
                  {[p.change_1d_pct, p.change_5d_pct, p.change_20d_pct].map((v, j) => (
                    <TableCell key={j} align="right" sx={{ color: (v ?? 0) >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>{fmtPct(v)}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {p.avg_value_20d_bn != null ? `${fmt(p.avg_value_20d_bn, 0)} ${t('common.units.billionShort', 'tỷ')}` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {flags.map(f => (
                        <Tooltip key={f.key} title={f.tip} arrow>
                          <Chip label={f.label} size="small" sx={{ bgcolor: f.bg, color: f.color, fontWeight: 700, fontSize: '0.55rem', height: 16 }} />
                        </Tooltip>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LeadersTable;
